import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../lib/auth';
import { API_BASE } from '../../lib/apiBase';

interface TwoFactorStatus {
  enabled: boolean;
  backup_codes_remaining: number;
}

interface SetupResponse {
  qr_code_uri: string;
  message: string;
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '12px 16px',
  borderRadius: 12,
  border: '1.5px solid #d1d5db',
  fontSize: 16,
  outline: 'none',
  boxSizing: 'border-box',
};

type Screen = 'status' | 'setup-qr' | 'setup-confirm' | 'backup-codes' | 'disable' | 'regen';

export function SecuritySettings() {
  const navigate = useNavigate();
  const accessToken = useAuthStore((s) => s.accessToken);

  const [status, setStatus] = useState<TwoFactorStatus | null>(null);
  const [screen, setScreen] = useState<Screen>('status');
  const [qrUri, setQrUri] = useState('');
  const [code, setCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken ?? ''}`,
  };

  async function fetchStatus() {
    try {
      const res = await fetch(`${API_BASE}/auth/2fa/status`, { headers });
      if (res.ok) setStatus(await res.json() as TwoFactorStatus);
    } catch { /* non-fatal */ }
  }

  useEffect(() => { void fetchStatus(); }, []);

  function reset() {
    setScreen('status');
    setCode('');
    setError('');
    setQrUri('');
    setBackupCodes([]);
    void fetchStatus();
  }

  async function startSetup() {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/auth/2fa/setup`, { method: 'POST', headers });
      const data = await res.json() as SetupResponse & { error?: { message: string } };
      if (!res.ok) { setError(data.error?.message ?? 'Setup failed.'); return; }
      setQrUri(data.qr_code_uri);
      setScreen('setup-qr');
    } catch { setError('Network error.'); }
    finally { setLoading(false); }
  }

  async function confirmSetup() {
    if (!code.trim()) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/auth/2fa/verify`, {
        method: 'POST', headers, body: JSON.stringify({ code: code.trim() }),
      });
      const data = await res.json() as { backup_codes?: string[]; error?: { message: string } };
      if (!res.ok) { setError(data.error?.message ?? 'Verification failed.'); return; }
      setBackupCodes(data.backup_codes ?? []);
      setScreen('backup-codes');
    } catch { setError('Network error.'); }
    finally { setLoading(false); }
  }

  async function disableTwoFactor() {
    if (!code.trim()) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/auth/2fa/disable`, {
        method: 'POST', headers, body: JSON.stringify({ code: code.trim() }),
      });
      const data = await res.json() as { error?: { message: string } };
      if (!res.ok) { setError(data.error?.message ?? 'Failed to disable 2FA.'); return; }
      reset();
    } catch { setError('Network error.'); }
    finally { setLoading(false); }
  }

  async function regenBackupCodes() {
    if (!code.trim()) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/auth/2fa/backup-codes`, {
        method: 'POST', headers, body: JSON.stringify({ code: code.trim() }),
      });
      const data = await res.json() as { backup_codes?: string[]; error?: { message: string } };
      if (!res.ok) { setError(data.error?.message ?? 'Failed to regenerate codes.'); return; }
      setBackupCodes(data.backup_codes ?? []);
      setCode('');
      setError('');
    } catch { setError('Network error.'); }
    finally { setLoading(false); }
  }

  return (
    <div style={{ maxWidth: 480, margin: '0 auto', padding: '24px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 28 }}>
        <button
          type="button"
          onClick={() => screen === 'status' ? navigate(-1) : reset()}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
          aria-label="Back"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <h1 style={{ fontSize: 20, fontWeight: 700, color: '#111827', margin: 0 }}>Security</h1>
      </div>

      {/* STATUS SCREEN */}
      {screen === 'status' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{
            background: '#fff', borderRadius: 16,
            border: '1px solid #e5e7eb', padding: 20,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div>
                <p style={{ margin: 0, fontWeight: 600, color: '#111827' }}>Two-factor authentication</p>
                <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6b7280' }}>
                  {status?.enabled
                    ? `Active — ${status.backup_codes_remaining} backup codes remaining`
                    : 'Add an extra layer of security to your account'}
                </p>
              </div>
              <span style={{
                padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                background: status?.enabled ? '#dcfce7' : '#f3f4f6',
                color: status?.enabled ? '#16a34a' : '#6b7280',
              }}>
                {status?.enabled ? 'On' : 'Off'}
              </span>
            </div>

            {status?.enabled ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 16 }}>
                <button
                  type="button"
                  onClick={() => { setScreen('regen'); setCode(''); setBackupCodes([]); }}
                  style={{
                    padding: '10px 16px', borderRadius: 10, border: '1.5px solid #d1d5db',
                    background: '#fff', color: '#374151', fontWeight: 500, cursor: 'pointer', fontSize: 14,
                  }}
                >
                  Regenerate backup codes
                </button>
                <button
                  type="button"
                  onClick={() => { setScreen('disable'); setCode(''); }}
                  style={{
                    padding: '10px 16px', borderRadius: 10, border: '1.5px solid #fecaca',
                    background: '#fff', color: '#dc2626', fontWeight: 500, cursor: 'pointer', fontSize: 14,
                  }}
                >
                  Disable 2FA
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => void startSetup()}
                disabled={loading}
                style={{
                  marginTop: 16, width: '100%', padding: '12px',
                  background: loading ? '#93c5fd' : '#1D4ED8',
                  color: '#fff', borderRadius: 12, fontWeight: 600,
                  fontSize: 15, border: 'none', cursor: loading ? 'not-allowed' : 'pointer',
                }}
              >
                {loading ? 'Setting up…' : 'Enable 2FA'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* SETUP — QR CODE SCREEN */}
      {screen === 'setup-qr' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div style={{
            background: '#fff', borderRadius: 16, border: '1px solid #e5e7eb', padding: 24, textAlign: 'center',
          }}>
            <p style={{ margin: '0 0 16px', fontWeight: 600, color: '#111827' }}>
              Scan this QR code with Google Authenticator
            </p>
            <div style={{
              background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 12,
              padding: 16, marginBottom: 16, wordBreak: 'break-all',
              fontSize: 11, color: '#6b7280', fontFamily: 'monospace',
            }}>
              {qrUri}
            </div>
            <p style={{ margin: 0, fontSize: 13, color: '#6b7280' }}>
              Can&apos;t scan? Copy the URI above into your authenticator app manually.
            </p>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 14, fontWeight: 500, color: '#374151', marginBottom: 6 }}>
              Enter the 6-digit code to confirm
            </label>
            <input
              type="tel"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="000000"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              maxLength={6}
              style={{ ...inputStyle, letterSpacing: '0.15em', textAlign: 'center' }}
            />
          </div>

          {error && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '10px 14px', color: '#dc2626', fontSize: 14 }}>
              {error}
            </div>
          )}

          <button
            type="button"
            onClick={() => void confirmSetup()}
            disabled={loading || code.length !== 6}
            style={{
              width: '100%', padding: '14px',
              background: loading || code.length !== 6 ? '#93c5fd' : '#1D4ED8',
              color: '#fff', borderRadius: 12, fontWeight: 600,
              fontSize: 16, border: 'none', cursor: loading ? 'not-allowed' : 'pointer',
            }}
          >
            {loading ? 'Verifying…' : 'Activate 2FA'}
          </button>
        </div>
      )}

      {/* BACKUP CODES SCREEN */}
      {screen === 'backup-codes' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div style={{
            background: '#fffbeb', border: '1.5px solid #fde68a', borderRadius: 16, padding: 20,
          }}>
            <p style={{ margin: '0 0 8px', fontWeight: 700, color: '#92400e', fontSize: 15 }}>
              Save your backup codes now
            </p>
            <p style={{ margin: 0, fontSize: 13, color: '#78350f' }}>
              These codes will not be shown again. Each code can only be used once.
            </p>
          </div>

          <div style={{
            background: '#fff', border: '1px solid #e5e7eb', borderRadius: 16,
            padding: 20, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10,
          }}>
            {backupCodes.map((bc) => (
              <span key={bc} style={{
                fontFamily: 'monospace', fontSize: 14, fontWeight: 600,
                color: '#111827', background: '#f9fafb', borderRadius: 8,
                padding: '8px 12px', letterSpacing: '0.1em',
              }}>
                {bc}
              </span>
            ))}
          </div>

          <button
            type="button"
            onClick={reset}
            style={{
              width: '100%', padding: '14px', background: '#1D4ED8',
              color: '#fff', borderRadius: 12, fontWeight: 600,
              fontSize: 16, border: 'none', cursor: 'pointer',
            }}
          >
            I&apos;ve saved my backup codes
          </button>
        </div>
      )}

      {/* DISABLE SCREEN */}
      {screen === 'disable' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{
            background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 16, padding: 20,
          }}>
            <p style={{ margin: 0, color: '#dc2626', fontWeight: 600 }}>Disable 2FA</p>
            <p style={{ margin: '8px 0 0', fontSize: 13, color: '#991b1b' }}>
              Enter your current authenticator code to confirm. Your account will be less secure without 2FA.
            </p>
          </div>

          <input
            type="tel"
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="000000"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            maxLength={6}
            style={{ ...inputStyle, letterSpacing: '0.15em', textAlign: 'center' }}
          />

          {error && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '10px 14px', color: '#dc2626', fontSize: 14 }}>
              {error}
            </div>
          )}

          <button
            type="button"
            onClick={() => void disableTwoFactor()}
            disabled={loading || code.length !== 6}
            style={{
              width: '100%', padding: '14px', background: '#dc2626',
              color: '#fff', borderRadius: 12, fontWeight: 600,
              fontSize: 16, border: 'none', cursor: loading ? 'not-allowed' : 'pointer',
            }}
          >
            {loading ? 'Disabling…' : 'Disable 2FA'}
          </button>
        </div>
      )}

      {/* REGEN BACKUP CODES SCREEN */}
      {screen === 'regen' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {backupCodes.length === 0 ? (
            <>
              <p style={{ margin: 0, color: '#374151', fontSize: 15 }}>
                Enter your authenticator code to generate new backup codes.
                <strong> Your existing backup codes will be invalidated.</strong>
              </p>
              <input
                type="tel"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="000000"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                maxLength={6}
                style={{ ...inputStyle, letterSpacing: '0.15em', textAlign: 'center' }}
              />
              {error && (
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '10px 14px', color: '#dc2626', fontSize: 14 }}>
                  {error}
                </div>
              )}
              <button
                type="button"
                onClick={() => void regenBackupCodes()}
                disabled={loading || code.length !== 6}
                style={{
                  width: '100%', padding: '14px', background: '#1D4ED8',
                  color: '#fff', borderRadius: 12, fontWeight: 600,
                  fontSize: 16, border: 'none', cursor: loading ? 'not-allowed' : 'pointer',
                }}
              >
                {loading ? 'Generating…' : 'Generate new codes'}
              </button>
            </>
          ) : (
            <>
              <div style={{
                background: '#fffbeb', border: '1.5px solid #fde68a', borderRadius: 16, padding: 20,
              }}>
                <p style={{ margin: '0 0 6px', fontWeight: 700, color: '#92400e', fontSize: 15 }}>Save your new backup codes</p>
                <p style={{ margin: 0, fontSize: 13, color: '#78350f' }}>Your old codes are now invalid.</p>
              </div>
              <div style={{
                background: '#fff', border: '1px solid #e5e7eb', borderRadius: 16,
                padding: 20, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10,
              }}>
                {backupCodes.map((bc) => (
                  <span key={bc} style={{
                    fontFamily: 'monospace', fontSize: 14, fontWeight: 600,
                    color: '#111827', background: '#f9fafb', borderRadius: 8,
                    padding: '8px 12px', letterSpacing: '0.1em',
                  }}>
                    {bc}
                  </span>
                ))}
              </div>
              <button
                type="button"
                onClick={reset}
                style={{
                  width: '100%', padding: '14px', background: '#1D4ED8',
                  color: '#fff', borderRadius: 12, fontWeight: 600,
                  fontSize: 16, border: 'none', cursor: 'pointer',
                }}
              >
                Done
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
